import { VerificationStatusType } from './General';
import { Resume } from './Resume';

export interface Rating {
  job_locator: string;
  job_title: string;
  client_locator: string;
  client_first_name: string;
  client_last_name: string;
  feedback: string;
  scores: Record<RatingFieldKey, number>;
  createdAt: string;
}

export interface Talent {
  locator: string;
  first_name: string;
  last_name: string;
  profile_picture?: string;
  work_type: string;
  email: string;
  company?: string;
  phone: string;
  country: string;
  id_type: string;
  id_number: string;
  verification_status: VerificationStatusType;
  resume_locator: string;
  resume: Resume;
  profile_completeness: string;
  applications: any[];
  bookmarked_jobs: string[];
  active_jobs: string[];
  title: string;
  date_of_birth: string;
  work_preferences: {
    industry: string;
    site: string;
    type: string;
  };
  id_verified: boolean;
  email_verified: boolean;
  currency: string;
  affiliate: Affiliate;
  job_invites: { job_locator: string; invite_date: string }[];
  notifications: {
    email: boolean;
    text: boolean;
    payment: boolean;
    messages: boolean;
    marketing: boolean;
    job: boolean;
    applicationInvite: boolean;
    applicationAccepted: boolean;
  };
  ratings: {
    history: [Rating];
    averageScores: Record<RatingFieldKey, number>;
  };
  active: boolean;
  is_hired: boolean;
}

export interface TalentRegistrationRequest {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  password: string;
  country: string;
  industry: string;
  title: string;
  date_of_birth: string;
}

export interface bookmarkJobRequest {
  locator: string;
  bookmarks: string[];
}

export enum WorkSite {
  'Remote',
  'In Person',
  'Hybrid',
}

export enum WorkType {
  'Project',
  'Part-time',
  'Full-time',
  'Flexible',
}

export interface WorkPreferences {
  industry: string;
  site: WorkSite;
  type: WorkType;
}

export interface TalentSearchRequest {
  searchString: string;
  skip?: number;
  take?: number;
}

export interface ComposeMessageClient {
  locator: string;
  first_name: string;
  last_name: string;
  profile_picture: string;
  title: string;
}

export interface TalentDashboardResponse {
  profileCompleteness: number;
  profileComplete: boolean;
  numberOfBookmarkedJobs: number;
  numberOfActiveJobs: number;
  lastBookmarkedJob?: {
    client: string;
    title: string;
  };
  numberOfApplications: number;
  lastApplication?: {
    client: string;
    applicationDate: string;
  };
  completedSections: {
    summary: boolean;
    languages: boolean;
    expertise: boolean;
    education: boolean;
    experience: boolean;
    workPreferences: boolean;
    profilePicture: boolean;
  };
}

export interface Affiliate {
  locator: string;
  name: string;
  code: string;
  domain: [string];
  industry: string;
}

export const ratingFields = [
  {
    key: 'communication',
    label: 'Communication',
    description:
      'Was the talent responsive, clear, and professional in their communication?',
  },
  {
    key: 'qualityOfWork',
    label: 'Quality of Work',
    description:
      'Did the final deliverable meet or exceed your expectations in terms of accuracy, completeness, and attention to detail?',
  },
  {
    key: 'timeliness',
    label: 'Timeliness',
    description:
      'Was the work delivered on time, or was there proactive communication about delays?',
  },
  {
    key: 'valueForMoney',
    label: 'Value for Money',
    description: 'Did the output reflect good value considering the cost?',
  },
  {
    key: 'professionalism',
    label: 'Professionalism & Attitude',
    description:
      'Was the talent respectful, collaborative, and easy to work with?',
  },
] as const;

export const feedbackField = {
  key: 'feedback',
  label: 'Feedback',
  description: '',
} as const;

export const getRatingFieldLabelByKey = (key: string): string | null => {
  const field = ratingFields.find(field => field.key === key);
  return field?.label || null;
};

export type RatingFieldKey = (typeof ratingFields)[number]['key'];
export type FeedbackFieldKey = typeof feedbackField.key;

export type RatingFormFields = {
  [K in RatingFieldKey]: number;
} & {
  [K in FeedbackFieldKey]: string;
};
